import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import SimpleProtectedRoute from './components/SimpleProtectedRoute';

// Pages
import HomePage from './pages/HomePage';
import RegistrationPage from './pages/RegistrationPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ConfirmSignupPage from './pages/ConfirmSignupPage';
import DashboardPage from './pages/DashboardPage';
import DashboardRoute from './pages/DashboardRoute.jsx';
import AnomalyRoute from './pages/AnomalyRoute';
import ChatbotRoute from './pages/ChatbotRoute';
import ChatbotPage from './pages/ChatbotPage';
import PricePage from './pages/PricePage';
import ContactPage from './pages/ContactPage';
import ProfilePage from './pages/ProfilePage'; 

// --- GDPR & Legal Pages ---
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ImprintPage from './pages/ImprintPage';
import CookieConsent from './components/CookieConsent';
import AnalyticsLoader from './components/AnalyticsLoader'; 
// --------------------------

// Contexts
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext'; 
import Joyride from 'react-joyride';
import { TutorialProvider, useTutorial } from './context/TutorialContext';
import { CookieProvider } from './context/CookieContext'; 

import './style/App.css';

function ProductProtectedRoute({ children, requiredProduct }) {
  const { user, userProducts, loading } = useAuth();

  if (loading) return <div>Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  
  const hasAccess = userProducts?.some(
    (p) => p.product_name === requiredProduct && p.is_active
  );

  if (!hasAccess) return <Navigate to="/price" replace />;
  return children;
}

function AppContent() {
  const { joyrideState, handleJoyrideCallback } = useTutorial();
  const { theme } = useTheme();
  const { plans } = useAuth();
  const location = useLocation();
  
  // This variable checks if we are on the chatbot route AND have an active plan.
  const isChatbotRoute = location.pathname === '/chatbot' || location.pathname.startsWith('/chatbot/');
  const isChatbotFullscreen = isChatbotRoute && !!plans?.chatbot?.active;

  function RootRedirect() {
    const { user, loading } = useAuth();
    if (loading) return null;
    return user ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />;
  }

  return (
    <>
      <Joyride
        {...joyrideState}
        key={theme} 
        callback={handleJoyrideCallback}
        styles={joyrideState.styles} 
      />

      <div className="app-container">
        {/* CHANGED: We now render Header unconditionally so it always shows, even for Pro users */}
        <Header />
        
        {/* The Gatekeeper: Loads scripts only if allowed */}
        <AnalyticsLoader />

        <main className="main-content">
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/register" element={<RegistrationPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/confirm-signup" element={<ConfirmSignupPage />} />
            
            <Route path="/dashboard" element={<DashboardRoute />} />
            
            <Route 
              path="/profile" 
              element={
                <SimpleProtectedRoute>
                  <ProfilePage />
                </SimpleProtectedRoute>
              } 
            />

            <Route path="/anomaly-detection" element={<AnomalyRoute />} />
            <Route path="/chatbot/*" element={<ChatbotPage />} />
            
            <Route path="/price" element={<PricePage />} />
            <Route path="/contact" element={<ContactPage />} />

            {/* --- LEGAL ROUTES --- */}
            <Route path="/datenschutz" element={<PrivacyPage />} />
            <Route path="/nutzungsbedingungen" element={<TermsPage />} />
            <Route path="/impressum" element={<ImprintPage />} />
            {/* -------------------- */}
          </Routes>
        </main>
        
        {/* Cookie Banner displays on top if no choice made - hidden in fullscreen chatbot to prevent clutter */}
        {!isChatbotFullscreen && <CookieConsent />}

        {!isChatbotFullscreen && <Footer />}
      </div>
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <TutorialProvider>
          {/* Wrap everything in CookieProvider so context is available everywhere */}
          <CookieProvider>
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </CookieProvider>
        </TutorialProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
